export interface HistogramBin {
  label: string
  count: number
  min: number
  max: number
}

const MAX_BINS = 12

/**
 * Create integer-aligned histogram bins, capped at MAX_BINS.
 * Each bin [n, n+1) gets label "n" (e.g. bar "2" includes values 2 to 2.99999).
 */
const createIntegerAlignedBins = (values: number[]): HistogramBin[] => {
  if (values.length === 0) {
    return []
  }

  const minVal = Math.floor(Math.min(...values))
  const maxVal = Math.floor(Math.max(...values))
  let numBins = maxVal - minVal + 1
  let binWidth = 1

  if (numBins > MAX_BINS) {
    binWidth = Math.max(1, Math.ceil(numBins / MAX_BINS))
    numBins = Math.min(MAX_BINS, Math.ceil((maxVal - minVal + 1) / binWidth))
  }

  if (numBins <= 0) {
    return [
      {
        label: minVal.toString(),
        count: values.length,
        min: minVal,
        max: minVal + 1,
      },
    ]
  }

  const bins: HistogramBin[] = Array.from({ length: numBins }, (_, i) => ({
    label: (minVal + i * binWidth).toString(),
    count: 0,
    min: minVal + i * binWidth,
    max: minVal + (i + 1) * binWidth,
  }))

  if (bins.length === 0) {
    return bins
  }

  values.forEach((value) => {
    let binIndex = Math.floor((value - minVal) / binWidth)
    if (binIndex < 0) {
      binIndex = 0
    }
    if (binIndex >= bins.length) {
      binIndex = bins.length - 1
    }
    bins[binIndex].count++
  })

  return bins
}

/**
 * Create decimal-aligned histogram bins for values whose range is < 1.
 * Picks a "nice" bin width and formats labels with appropriate decimal precision.
 */
const createDecimalAlignedBins = (values: number[]): HistogramBin[] => {
  if (values.length === 0) {
    return []
  }

  const minVal = Math.min(...values)
  const maxVal = Math.max(...values)
  const range = maxVal - minVal

  if (range === 0) {
    return [
      {
        label: minVal.toFixed(2),
        count: values.length,
        min: minVal,
        max: minVal,
      },
    ]
  }

  const rawBinWidth = range / MAX_BINS
  const magnitude = 10 ** Math.floor(Math.log10(rawBinWidth))
  const niceBinWidth = Math.ceil(rawBinWidth / magnitude) * magnitude

  const binStart = Math.floor(minVal / niceBinWidth) * niceBinWidth
  const numBins = Math.min(
    MAX_BINS,
    Math.ceil((maxVal - binStart) / niceBinWidth),
  )
  const decimals = Math.max(2, Math.ceil(-Math.log10(niceBinWidth)))

  const bins: HistogramBin[] = Array.from({ length: numBins }, (_, i) => {
    const binMin = Number((binStart + i * niceBinWidth).toPrecision(15))
    const binMax = Number((binStart + (i + 1) * niceBinWidth).toPrecision(15))
    return {
      label: binMin.toFixed(decimals),
      count: 0,
      min: binMin,
      max: binMax,
    }
  })

  if (bins.length === 0) {
    return bins
  }

  values.forEach((value) => {
    let binIndex = Math.floor((value - binStart) / niceBinWidth)
    if (binIndex < 0) {
      binIndex = 0
    }
    if (binIndex >= bins.length) {
      binIndex = bins.length - 1
    }
    bins[binIndex].count++
  })

  return bins
}

/**
 * Format a histogram bin as a "min–max" range string with consistent decimal precision.
 * Derives decimal places from the bin label (which encodes the precision used during binning).
 * Appends units suffix when provided.
 */
export const formatBinRange = (
  min: number,
  max: number,
  label: string,
  units?: string,
): string => {
  const decimals = label.includes(".") ? label.split(".")[1].length : 0
  const fmt = (v: number) => (decimals > 0 ? v.toFixed(decimals) : v.toString())
  const suffix = units ? ` ${units}` : ""
  return `Range: ${fmt(min)}–${fmt(max)}${suffix}`
}

/**
 * Select integer or decimal binning based on whether all values share the same integer floor.
 * Use this when you have raw numeric values and want the chart to handle layout automatically.
 */
export const createHistogramBins = (values: number[]): HistogramBin[] => {
  if (values.length === 0) {
    return []
  }
  const minFloored = Math.floor(Math.min(...values))
  const maxFloored = Math.floor(Math.max(...values))
  return minFloored === maxFloored
    ? createDecimalAlignedBins(values)
    : createIntegerAlignedBins(values)
}
